import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Parser from 'html-react-parser';
import l from '../../../../lang';
import { Routes } from '../../../../config/routes';
import ButtonLink from '../../../common/button-link';
import CardTitle from '../../../common/card-title';
import Card from '../../../common/card';
import CardInfo from '../../../common/card-info';
import { segmentHomeKycStep2Complete, segmentHomeQuickLinkEditCountries } from '../../../../utils/analytics';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Button } from '../../../common/button';
import { Tooltip } from 'react-tooltip';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { useCompanyInfo } from '../../../../store/company-information';
import { useEffect, useState } from 'react';
import { AuthSelectors } from '../../../../store/auth/selectors';
import { canEditCompanyInformation } from '../../../../store/company-information/roles';
import { useDashboard } from '../../../../store/dashboard';

interface FirstTimeCardsProps {
  shouldAnimationsRun: boolean;
  maxAmount: number;
}

const FirstTimeCards = ({ shouldAnimationsRun, maxAmount }: FirstTimeCardsProps) => {
  const history = useHistory();
  const roles = useSelector(AuthSelectors.getRoles);
  const canEdit = canEditCompanyInformation(roles);
  const { getKycDocuments, itHasKycDocsToUpdate: itHasKycDocsToUpdateFromCompanyInfo } = useCompanyInfo();
  const { merchantMe } = useDashboard();
  const [itHasKycDocsToUpdate, setItHasKycDocsToUpdate] = useState(false);

  useEffect(() => {
    getKycDocuments();
  }, []);

  useEffect(() => {
    setItHasKycDocsToUpdate(
      itHasKycDocsToUpdateFromCompanyInfo || merchantMe?.legalTalentSessionStatus == 'awaiting_client_correction'
    );
  }, [merchantMe?.legalTalentSessionStatus, itHasKycDocsToUpdateFromCompanyInfo]);

  const onPaymentMethodsHandler = () => {
    segmentHomeQuickLinkEditCountries();
    history.push(Routes.PAYMENT_METHODS);
  };

  const onCompanyInformationHandler = () => {
    segmentHomeKycStep2Complete();
    history.push(`${Routes.COMPANY_INFORMATION}?kyc=2`);
  };

  const onReviewDocumentationHandler = () => {
    if (merchantMe?.legalTalentSessionStatus == 'awaiting_client_correction') {
      history.push(`${Routes.COMPANY_INFORMATION}?kyc=2`);
    } else {
      history.push(Routes.REVIEW_DOCUMENTATION);
    }
  };

  return (
    <Card>
      <CardTitle>
        {itHasKycDocsToUpdate ? (
          <span style={{ fontSize: '22px' }}>{Parser(l('home.readyMsg.review-docs'))}</span>
        ) : (
          <span style={{ fontSize: '22px' }}>{Parser(l('home.readyMsg.firstTime'))}</span>
        )}
      </CardTitle>
      <div className={`two-cards ${shouldAnimationsRun ? 'card__animated' : ''}`}>
        <CardInfo bottomElement={<div className="card__accentuate-line" />}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress variant="determinate" value={itHasKycDocsToUpdate ? 90 : 75} size={100} />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span className="circular-progress__text">{itHasKycDocsToUpdate ? '90%' : '75%'}</span>
              </Box>
            </Box>
            <div className="card__desc">{Parser(l('home.complete-profile.kyc2.desc'))}</div>
          </Box>
        </CardInfo>

        <CardInfo bottomElement={<div className="card__accentuate-line" />}>
          <div className={'card__desc step2__desc' + (itHasKycDocsToUpdate ? ' step2__desc--kyc' : '')}>
            <div className="step2__desc_text">
              <div>
                {itHasKycDocsToUpdate ? (
                  <>
                    <h4>{l('home.readyMsg.reviewDocs.title')}</h4>
                    {Parser(l('home.readyMsg.reviewDocs.desc'))}
                  </>
                ) : (
                  <div>
                    <ButtonLink
                      text={l('home.readyMsg.paso2.link')}
                      onClick={onCompanyInformationHandler}
                      disabled={!canEdit}
                    />
                    {Parser(l('home.readyMsg.paso2.desc'))}
                  </div>
                )}
              </div>
              {!itHasKycDocsToUpdate && (
                <>
                  <a id="clickable">
                    <InfoIcon />
                  </a>

                  <Tooltip anchorSelect="#clickable" clickable place="right">
                    <div className="benefits__desc">
                      <span>{l('home.readyMsg.step2.tooltip.title')}</span>
                      <ul>
                        <li>{l('home.readyMsg.step2.tooltip.item1')}</li>
                        <li>{l('home.readyMsg.step2.tooltip.item2')}</li>
                      </ul>
                      <span>
                        {l('home.readyMsg.step2.tooltip.desc1')}
                        <ButtonLink text={l('home.readyMsg.step2.tooltip.disable')} onClick={onPaymentMethodsHandler} />
                        {l('home.readyMsg.step2.tooltip.desc2')}
                      </span>
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
            {itHasKycDocsToUpdate ? (
              <Button onClick={onReviewDocumentationHandler}>{l('home.readyMsg.step2.review-docs')}</Button>
            ) : (
              <Button onClick={onCompanyInformationHandler} disabled={!canEdit}>
                {l('home.readyMsg.step2.complete-now')}
              </Button>
            )}
          </div>
        </CardInfo>
      </div>
    </Card>
  );
};

export default FirstTimeCards;
